import { NOTE_BODY_MAX_LENGTH } from '@kanto/shared';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useT } from '../lib/i18n';
import { renderMarkdown } from '../lib/markdown';

/**
 * Where a note is written: a `<textarea>`, and a switch that shows what the
 * markdown becomes.
 *
 * **There is no save button, and that is the design.** A note is not a form to
 * submit — it is a thing being written, and the moment somebody wonders whether
 * their words are safe is the moment the editor has failed. It saves
 * `SAVE_AFTER_MS` after the last keystroke, and again on the way out of the
 * field, which covers the case the timer cannot: a tab closed, or a link
 * followed, within a second and a half of typing.
 *
 * The consequence is stated plainly in `specs/01`: **there is no version
 * history.** Autosave overwrites, and what was there before is gone.
 *
 * The draft lives here rather than in the query cache, and **nothing puts the
 * server's answer back into the field**. A note being typed into is the one
 * thing on screen the server does not own: every save answers with the body it
 * stored, and copying that back would throw away whatever was typed while the
 * request was in flight. Opening a different note resets the draft by remounting
 * the component — `key={note.id}` on the page above — which is the one event
 * that should reset it.
 */

/**
 * How long the typing has to stop before a save goes out.
 *
 * A second and a half is past the pause between two words and short of the pause
 * between two thoughts. Shorter turns a paragraph into a dozen requests; longer
 * and the gap between "I stopped typing" and "it is saved" becomes long enough
 * to notice, which is the anxiety this is meant to remove.
 */
const SAVE_AFTER_MS = 1500;

export type SaveState = 'idle' | 'saving' | 'saved' | 'failed';

export function NoteEditor({
  initialBody,
  onSave,
  onDraft,
  state,
}: {
  initialBody: string;
  onSave: (body: string) => void;
  /**
   * Every keystroke, so the heading above can follow the first line as it is
   * typed. Separate from `onSave` because they answer different questions —
   * "what does this note say now" and "what should be stored" — and conflating
   * them would either save on every character or leave the title a save behind.
   */
  onDraft: (body: string) => void;
  state: SaveState;
}): ReactElement {
  const t = useT();
  const [body, setBody] = useState(initialBody);
  const [preview, setPreview] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  /** What was last handed to `onSave`, so an unchanged body is not resaved. */
  const sent = useRef(initialBody);

  // A pending save would otherwise fire after the screen has gone, against a
  // note nobody is looking at any more.
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const save = (next: string): void => {
    window.clearTimeout(timer.current);
    if (next === sent.current) return;
    sent.current = next;
    onSave(next);
  };

  const onChange = (next: string): void => {
    setBody(next);
    onDraft(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => save(next), SAVE_AFTER_MS);
  };

  // What is on screen is not what was last sent, so the last answer no longer
  // describes it. Saying "Saved" over an edited paragraph is the one thing an
  // autosaving editor must never do.
  const shown: SaveState = body === sent.current ? state : 'idle';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-2 flex items-center gap-2">
        <div role="group" aria-label={t('notes.view')} className="flex items-center gap-1">
          <ViewTab label={t('notes.write')} pressed={!preview} onClick={() => setPreview(false)} />
          <ViewTab label={t('notes.preview')} pressed={preview} onClick={() => setPreview(true)} />
        </div>

        {/* `aria-live` on a region that is always there rather than one inserted
            with its text: an element added along with its message is announced
            inconsistently, while one already in the page is announced every time
            it fills. The same reasoning as the sign-in screen's error region. */}
        <p aria-live="polite" className="ml-auto text-xs text-ink-400">
          {shown === 'saving' && t('notes.saving')}
          {shown === 'saved' && t('notes.saved')}
          {shown === 'failed' && <span className="text-alert">{t('notes.saveFailed')}</span>}
        </p>
      </div>

      {preview ? (
        <div
          className="prose-note min-h-64 flex-1 overflow-auto rounded-lg border border-line bg-surface px-4 py-3"
          // Sanitised by `lib/markdown.ts`, which runs DOMPurify over whatever
          // `marked` produced — and the Content-Security-Policy is the second
          // line behind it (D260815c).
          dangerouslySetInnerHTML={{ __html: renderMarkdown(body) }}
        />
      ) : (
        <textarea
          value={body}
          maxLength={NOTE_BODY_MAX_LENGTH}
          onChange={(event) => onChange(event.target.value)}
          onBlur={() => save(body)}
          placeholder={t('notes.placeholder')}
          aria-label={t('notes.body')}
          spellCheck
          className="min-h-64 flex-1 resize-none rounded-lg border border-line bg-surface px-4 py-3 font-mono text-sm leading-relaxed outline-none focus:border-accent"
        />
      )}
    </div>
  );
}

/**
 * `aria-pressed` and not a tab, for the reason the list filters are not tabs:
 * these are two states of one thing rather than two places to be.
 */
function ViewTab({
  label,
  pressed,
  onClick,
}: {
  label: string;
  pressed: boolean;
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={`flex min-h-9 items-center rounded-lg px-3 text-sm font-medium transition-colors ${
        pressed ? 'bg-accent-soft text-accent' : 'text-ink-500 hover:text-ink-900'
      }`}
    >
      {label}
    </button>
  );
}
